import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from './ThemeContext';
import './App.css';

// Helper function for text truncation with punctuation detection (comma or period)
function findFullstopInText(text, startPos, endPos) {
  if (startPos >= text.length) return -1;
  const searchEnd = Math.min(endPos, text.length);
  for (let i = startPos; i < searchEnd; i++) {
    if (text[i] === '.' || text[i] === ',') return i;
  }
  return -1;
}

// Transform raw product data to snapshot_api_product format
function transformProductData(rawProduct) {
  const currentTime = new Date().toISOString().slice(0, 19).replace('T', ' ');

  // Extract data from raw product
  const productData = {
    id: null, // Auto-increment
    product_id: (rawProduct.Id || '').replace(/^product_?/, ''), // Remove "product_" prefix
    title: (rawProduct.Name || '').substring(0, 120), // 120 characters max
    product_status: 'STAGING', // Default value
    created_at: currentTime,
    updated_at: currentTime,
    discount_price: rawProduct.CurrentPrice || '0', // Current Price
    main_price: rawProduct.OriginalPrice || '0',    // Original Price
    store_id: '', // Editable field, leave blank
    store_name: rawProduct.CampaignName || '', // Campaign Name
    cat_id_1: '', // Editable field, leave blank
    cat_id_2: '', // Editable field, leave blank
    cat_id_3: '', // Editable field, leave blank
    product_url: rawProduct.Url || '', // Url field
    product_img: rawProduct.ImageUrl || '', // Image field
    Gtin: rawProduct.Gtin || '',
    Mpn: rawProduct.Mpn || ''
  };

  // Handle description with smart truncation
  let fullText = rawProduct.Description || '';
  let description = '';
  let specification = '';
  let features = '';

  // Step 1: Extract description (first 250 chars or until punctuation)
  if (fullText.length <= 250) {
    description = fullText;
  } else {
    const breakPoint = findFullstopInText(fullText, 200, 250);
    if (breakPoint !== -1) {
      description = fullText.substring(0, breakPoint + 1);
    } else {
      description = fullText.substring(0, 250);
    }
  }

  // Step 2: Extract specification from remaining text
  let remaining = fullText.substring(description.length);
  if (remaining.trim()) {
    if (remaining.length <= 500) {
      specification = remaining;
    } else {
      const breakPoint = findFullstopInText(remaining, 300, 500);
      if (breakPoint !== -1) {
        specification = remaining.substring(0, breakPoint + 1);
      } else {
        specification = remaining.substring(0, 500);
      }
    }
  }

  // Step 3: Extract features from remaining text
  remaining = remaining.substring(specification.length);
  if (remaining.trim()) {
    if (remaining.length <= 500) {
      features = remaining;
    } else {
      const breakPoint = findFullstopInText(remaining, 300, 500);
      if (breakPoint !== -1) {
        features = remaining.substring(0, breakPoint + 1);
      } else {
        features = remaining.substring(0, 500);
      }
    }
  }

  productData.description = description.trim();
  productData.specification = specification.trim();
  productData.features = features.trim();

  // Parse category by ">" delimiter
  const categoryStr = rawProduct.Category || '';
  if (categoryStr) {
    const categories = categoryStr.split('>')
      .map(cat => cat.trim())
      .filter(cat => cat);

    if (categories.length >= 1) {
      productData.cat_name_1 = categories[0];
    }
    if (categories.length >= 2) {
      productData.cat_name_2 = categories[1];
    }
    if (categories.length >= 3) {
      productData.cat_name_3 = categories[2];
    }
    // Discard any categories beyond the third
  }

  // Main product image (SINGLE IMAGE ONLY)
  let mainImageUrl = '';
  if (Array.isArray(rawProduct.ImageUrl)) {
    // If it's an array, take the first element
    mainImageUrl = rawProduct.ImageUrl[0] || '';
  } else if (typeof rawProduct.ImageUrl === 'string') {
    // If it's a string, take the part before first comma OR space OR semicolon
    const withoutCommas = rawProduct.ImageUrl.split(',')[0].trim();
    const withoutSpaces = withoutCommas.split(' ')[0].trim();
    const withoutSemicolons = withoutSpaces.split(';')[0].trim();
    mainImageUrl = withoutSemicolons;
  } else {
    // Otherwise, convert to string and clean it
    mainImageUrl = String(rawProduct.ImageUrl || '').split(',')[0].trim().split(' ')[0].trim().split(';')[0].trim();
  }
  productData.product_img = mainImageUrl;

  // Handle product_images JSON structure (additional images only, max 5)
  const additionalImages = Array.isArray(rawProduct.AdditionalImageUrls)
    ? rawProduct.AdditionalImageUrls.slice(0, 5)  // Limit to max 5 images
    : (typeof rawProduct.AdditionalImageUrls === 'string'
        ? rawProduct.AdditionalImageUrls.split(',').map(url => url.trim()).slice(0, 5)
        : []);

  const productImages = [{"images": additionalImages}];
  productData.product_images = JSON.stringify(productImages);

  // Handle product_variants JSON structure
  // Handle both comma-separated and "and"-separated color strings
  let colors = [];
  if (Array.isArray(rawProduct.Colors)) {
    colors = rawProduct.Colors.flatMap(color => {
      if (typeof color === 'string') {
        // Handle comma separation
        if (color.includes(',')) {
          return color.split(',').map(c => c.trim());
        }
        // Handle "and" separation
        if (color.includes(' and ')) {
          return color.split(' and ').map(c => c.trim());
        }
        // Single color
        return [color.trim()];
      }
      return [];
    });
  } else if (typeof rawProduct.Colors === 'string') {
    // Handle comma separation
    if (rawProduct.Colors.includes(',')) {
      colors = rawProduct.Colors.split(',').map(c => c.trim());
    }
    // Handle "and" separation
    else if (rawProduct.Colors.includes(' and ')) {
      colors = rawProduct.Colors.split(' and ').map(c => c.trim());
    } else {
      colors = [rawProduct.Colors.trim()];
    }
  }

  const sizes = rawProduct.Size || '';
  const sizesList = sizes.includes(',')
    ? sizes.split(',').map(s => s.trim()).filter(s => s)
    : (sizes ? [sizes.trim()] : []);

  const productVariants = [{"colors": colors, "sizes": sizesList}];
  productData.product_variants = JSON.stringify(productVariants);

  return productData;
}

function ProductUpload() {
  const navigate = useNavigate();
  const { theme, toggleTheme, isDark } = useTheme();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(50); // Page size state, max 500
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render key

  // Row selection state for bulk operations
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Dropdown visibility states (only used for cat_id_3)
  const [showCatId3Dropdown, setShowCatId3Dropdown] = useState(null);

  // Custom input states (only used for cat_id_3)
  const [customCatId3, setCustomCatId3] = useState('');
  const [showCustomCat3Input, setShowCustomCat3Input] = useState(false);

  // Bulk operation states
  const [bulkStoreId, setBulkStoreId] = useState('');
  const [bulkCatId1, setBulkCatId1] = useState('');
  const [bulkCatId2, setBulkCatId2] = useState('');
  const [bulkCatId3, setBulkCatId3] = useState('');
  const [bulkProductStatus, setBulkProductStatus] = useState('');



  // Modal state for image preview
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState([]);
  const [selectedImageAlt, setSelectedImageAlt] = useState('');

  useEffect(() => {
    // Get product data from sessionStorage (set by Products.js)
    try {
      const productData = sessionStorage.getItem('productsUploadData');
      if (productData) {
        const parsedData = JSON.parse(productData);
        // Transform raw data to snapshot_api_product format
        const transformedData = parsedData.map(rawProduct => transformProductData(rawProduct));
        console.log('Transformed products:', transformedData.slice(0, 3)); // Debug first 3 items
        setProducts(transformedData);
        setTotalPages(Math.ceil(transformedData.length / pageSize)); // Using dynamic page size
      } else {
        setError('No product data found. Please go back and click "Products Upload Table" first.');
      }
    } catch (err) {
      console.error('Error accessing sessionStorage:', err);
      setError('Error loading product data. Please try again.');
    }
  }, []);

  // Close dropdowns when clicking outside (only for cat_id_3)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideCat3 = !event.target.closest('.cat-id-3-button') && !event.target.closest('.cat-id-3-dropdown') && !event.target.closest('.custom-cat3-input-container');

      if (isOutsideCat3) {
        setShowCatId3Dropdown(null);
        setShowCustomCat3Input(false);
        setCustomCatId3('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update select all state when page changes
  React.useEffect(() => {
    updateSelectAllState();
  }, [currentPage, products, selectedRows]);

  const handleUpdateStoreId = (index, newStoreId) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < products.length) {
      const updatedProducts = [...products];
      updatedProducts[globalIndex].store_id = newStoreId;
      setProducts(updatedProducts);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  const handleUpdateCatId1 = (index, newCatId) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < products.length) {
      const updatedProducts = [...products];
      updatedProducts[globalIndex].cat_id_1 = newCatId;
      setProducts(updatedProducts);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  const handleUpdateCatId2 = (index, newCatId) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < products.length) {
      const updatedProducts = [...products];
      updatedProducts[globalIndex].cat_id_2 = newCatId;
      setProducts(updatedProducts);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  const handleUpdateCatId3 = (index, newCatId) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < products.length) {
      const updatedProducts = [...products];
      updatedProducts[globalIndex].cat_id_3 = newCatId;
      setProducts(updatedProducts);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  // Handler for custom input submission (only for cat_id_3)
  const handleCustomCat3IdSubmit = (index) => {
    if (!customCatId3.trim()) return;
    handleUpdateCatId3(index, customCatId3.trim());
    setShowCustomCat3Input(false);
    setCustomCatId3('');
  };

  // Available options (only needed for cat_id_3)
  const catIdOptions = ['custom', 'CAT001', 'CAT002', 'CAT003', 'ELECTRONICS', 'CLOTHING', 'HOME'];

  // Handle page size change
  const handlePageSizeChange = () => {
    const newPageSize = parseInt(pageSize);
    if (isNaN(newPageSize) || newPageSize < 1 || newPageSize > 500) {
      showMessage('Page size must be between 1 and 500', 'error');
      setPageSize(50); // Reset to default
      return;
    }

    // Update pagination calculations
    setTotalPages(Math.ceil(products.length / newPageSize));

    // Reset to first page when changing page size
    setCurrentPage(1);

    // Clear row selection when page size changes
    setSelectedRows(new Set());
    setSelectAll(false);

    showMessage(`Page size set to ${newPageSize} products per page`, 'success');
  };

  const removeProduct = (index) => {
    const globalIndex = (currentPage - 1) * pageSize + index;
    const updatedProducts = [...products];
    updatedProducts.splice(globalIndex, 1);
    setProducts(updatedProducts);
    setTotalPages(Math.ceil(updatedProducts.length / pageSize));
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  };

  // Image preview modal handlers
  const handleImageClick = (imageArray, imageAlt, mainImage) => {
    try {
      // Parse product_images JSON to get additional images
      const productData = JSON.parse(imageArray);
      const additionalImages = productData && productData[0] && productData[0].images ? productData[0].images : [];

      // Combine main image with additional images
      const allImages = [mainImage, ...additionalImages].filter(Boolean);
      setSelectedImage(allImages);
    } catch {
      // Fallback - just show main image
      setSelectedImage([mainImage].filter(Boolean));
    }
    setSelectedImageAlt(imageAlt);
    setShowModal(true);
  };

  const handleUpdateTitle = (index, newTitle) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < products.length) {
      const updatedProducts = [...products];
      updatedProducts[globalIndex].title = newTitle;
      setProducts(updatedProducts);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  const handleUpdateDescription = (index, newDescription) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < products.length) {
      const updatedProducts = [...products];
      updatedProducts[globalIndex].description = newDescription;
      setProducts(updatedProducts);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  const handleUpdateSpecification = (index, newSpecification) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < products.length) {
      const updatedProducts = [...products];
      updatedProducts[globalIndex].specification = newSpecification;
      setProducts(updatedProducts);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  const handleUpdateFeatures = (index, newFeatures) => {
    const globalIndex = (currentPage - 1) * pageSize + index;

    if (globalIndex >= 0 && globalIndex < products.length) {
      const updatedProducts = [...products];
      updatedProducts[globalIndex].features = newFeatures;
      setProducts(updatedProducts);
      setRefreshKey(prev => prev + 1); // Force re-render
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedImage([]);
    setSelectedImageAlt('');
  };

  // Row selection functions
  const handleRowSelection = (globalIndex) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(globalIndex)) {
      newSelectedRows.delete(globalIndex);
    } else {
      newSelectedRows.add(globalIndex);
    }
    setSelectedRows(newSelectedRows);
    updateSelectAllState(newSelectedRows);
  };

  const updateSelectAllState = (selectedSet = selectedRows) => {
    const currentPageData = getCurrentPageData();
    const currentPageGlobalIndices = currentPageData.map((_, index) =>
      (currentPage - 1) * pageSize + index
    );
    const allCurrentPageSelected = currentPageGlobalIndices.every(index => selectedSet.has(index));
    setSelectAll(allCurrentPageSelected);
  };

  const handleSelectAll = () => {
    const currentPageData = getCurrentPageData();
    const currentPageGlobalIndices = currentPageData.map((_, index) =>
      (currentPage - 1) * pageSize + index
    );

    const newSelectedRows = new Set(selectedRows);
    const allCurrentPageSelected = currentPageGlobalIndices.every(index => newSelectedRows.has(index));

    if (allCurrentPageSelected) {
      // Deselect all items on current page
      currentPageGlobalIndices.forEach(index => newSelectedRows.delete(index));
    } else {
      // Select all items on current page
      currentPageGlobalIndices.forEach(index => newSelectedRows.add(index));
    }

    setSelectedRows(newSelectedRows);
    setSelectAll(!allCurrentPageSelected);
  };

  // Bulk operation handlers
  const handleBulkStoreIdChange = () => {
    if (!bulkStoreId.trim()) return;

    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : products.map((_, index) => index);
    const updatedProducts = [...products];

    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < products.length) {
        updatedProducts[globalIndex].store_id = bulkStoreId.trim();
      }
    });

    setProducts(updatedProducts);
    setRefreshKey(prev => prev + 1); // Force re-render
    showMessage(`✅ Applied store_id "${bulkStoreId}" to ${affectedIndices.length} products`, 'success');
  };

  const handleBulkCatId1Change = () => {
    if (!bulkCatId1.trim()) return;

    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : products.map((_, index) => index);
    const updatedProducts = [...products];

    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < products.length) {
        updatedProducts[globalIndex].cat_id_1 = bulkCatId1.trim();
      }
    });

    setProducts(updatedProducts);
    setRefreshKey(prev => prev + 1); // Force re-render
    showMessage(`✅ Applied cat_id_1 "${bulkCatId1}" to ${affectedIndices.length} products`, 'success');
  };

  const handleBulkCatId2Change = () => {
    if (!bulkCatId2.trim()) return;

    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : products.map((_, index) => index);
    const updatedProducts = [...products];

    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < products.length) {
        updatedProducts[globalIndex].cat_id_2 = bulkCatId2.trim();
      }
    });

    setProducts(updatedProducts);
    setRefreshKey(prev => prev + 1); // Force re-render
    showMessage(`✅ Applied cat_id_2 "${bulkCatId2}" to ${affectedIndices.length} products`, 'success');
  };

  const handleBulkCatId3Change = () => {
    if (!bulkCatId3.trim()) return;

    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : products.map((_, index) => index);
    const updatedProducts = [...products];

    affectedIndices.forEach(globalIndex => {
      if (globalIndex >= 0 && globalIndex < products.length) {
        updatedProducts[globalIndex].cat_id_3 = bulkCatId3.trim();
      }
    });

    setProducts(updatedProducts);
    setRefreshKey(prev => prev + 1); // Force re-render
    showMessage(`✅ Applied cat_id_3 "${bulkCatId3}" to ${affectedIndices.length} products`, 'success');
  };

  const handleBulkProductStatusChange = () => {
    if (!bulkProductStatus.trim()) return;

    const affectedIndices = selectedRows.size > 0 ? Array.from(selectedRows) : products.map((_, index) => index);
    const updatedProducts = [...products];

    affectedIndices.forEach(globalIndex => {
        if (globalIndex >= 0 && globalIndex < products.length) {
            updatedProducts[globalIndex].product_status = bulkProductStatus.trim();
        }
    });

    setProducts(updatedProducts);
    setRefreshKey(prev => prev + 1); // Force re-render
    showMessage(`✅ Applied product_status "${bulkProductStatus}" to ${affectedIndices.length} products`, 'success');
  };

  // Function to display messages
  const showMessage = (message, type = 'success') => {
    setError('');
    if (type === 'success') {
      setSuccessMessage(message);
    } else {
      setError(message);
    }
  };

  const handleUpload = async () => {
    setSuccessMessage('');
    setError('');
    setErrorDetails([]);

    setLoading(true);

    try {
      console.log('🎯 DATABASE: Starting database upload for', products.length, 'products');

      // Send directly to database API (products are already transformed)
      const response = await axios.post('/api/products/upload/', {
        products: products
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 120000, // 2 minute timeout for large uploads
      });

      console.log('📊 DATABASE: API Response:', response.data);

      if (response.data.success) {
        console.log('✅ DATABASE: Successfully uploaded', response.data.uploaded_count, 'products to database');

        const successMsg = `✅ Successfully uploaded ${response.data.uploaded_count} products to database at ${response.data.timestamp}`;
        showMessage(successMsg, 'success');

      } else {
        console.error('❌ DATABASE: Upload failed:', response.data.error);

        // Check if detailed error information is available
        if (response.data.details && Array.isArray(response.data.details) && response.data.details.length > 0) {
          console.error('❌ DATABASE: Detailed errors:', response.data.details);

          // Set main error message and detailed error list separately
          const errorMessage = response.data.error || 'Upload completed with errors';
          setError(errorMessage);
          setErrorDetails(response.data.details);
        } else {
          // Fallback to simple error message
          const errorMsg = response.data.error || 'Upload failed';
          setError(errorMsg);
          setErrorDetails([]);
        }
      }

    } catch (err) {
      console.error('❌ DATABASE: Error during database upload:', err);

      let errorMessage = 'Failed to upload products to database';
      let details = [];

      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
        if (err.response.data.details) {
          details = err.response.data.details;
        }
      }

      setError(errorMessage);
      setErrorDetails(details);
    }

    setLoading(false);
  };

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return products.slice(startIndex, endIndex);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    let items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <button
          key={number}
          className={`mx-1 px-3 py-1 rounded ${
            number === currentPage
              ? 'bg-perzsi-yellow text-perzsi-gray font-semibold'
              : 'bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50'
          }`}
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </button>
      );
    }

    return (
      <div className="flex justify-center items-center mt-6">
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        >
          First
        </button>
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {items}
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
        <button
          className="mx-1 px-3 py-1 rounded bg-white text-perzsi-gray border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        >
          Last
        </button>
      </div>
    );
  };

  if (error && !products.length) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
          <button
            className="perzsi-button-secondary mt-4"
            onClick={() => navigate('/store-products')}
          >
            Go Back to Store Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className="perzsi-gradient shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <img
                  src="/perzsi-icon-black.png"
                  alt="Perzsi.com"
                  className="h-12 w-auto"
                />
                <p className="text-yellow-100">Products Upload Table</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-white text-right">
                <p className="text-sm opacity-90">Transformed Product Data</p>
                <p className="text-xs opacity-75">snapshot_api_product Table</p>
              </div>
              <button
                onClick={toggleTheme}
                className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg transition-colors duration-200"
                title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
              >
                {isDark ? '☀️' : '🌙'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="w-full px-4 py-8">
        {/* Control Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>
              Products Upload Table
            </h3>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              Review and modify the transformed product data before uploading to the database.
            </p>
            <button
              className="perzsi-button-primary w-full"
              onClick={handleUpload}
              disabled={loading || products.length === 0}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-perzsi-gray" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                `Upload ${products.length} Products`
              )}
            </button>

            {successMessage && (
              <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg animate-fade-in">
                <div className="flex items-center">
                  <span className="text-lg mr-2">✅</span>
                  <span className="text-sm font-medium">{successMessage}</span>
                </div>
              </div>
            )}
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>
              Upload Summary
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Data Source:</span>
                <span className="font-semibold">Session</span>
              </div>
              <div className="flex justify-between">
                <span>Total Products:</span>
                <span className="font-semibold">{products.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Status:</span>
                <span className="font-semibold text-blue-600">STAGING</span>
              </div>
              {products.length > 0 && (
                <div className="flex justify-between">
                  <span>Store Names:</span>
                  <span className="font-semibold">{new Set(products.map(p => p.store_name).filter(Boolean)).size}</span>
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t">
              <button
                className="perzsi-button-secondary w-full"
                onClick={() => navigate('/store-products')}
              >
                ← Back to Store Products
              </button>
            </div>
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>
              Bulk Operations
            </h3>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4 text-sm`}>
              Apply changes to selected rows (or all rows if nothing selected):
            </p>

            <div className="space-y-4">
              {/* Store ID Bulk Input */}
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  placeholder="Store ID"
                  value={bulkStoreId}
                  onChange={(e) => setBulkStoreId(e.target.value)}
                  className="perzsi-input flex-1"
                />
                <button
                  onClick={handleBulkStoreIdChange}
                  className="perzsi-button-primary"
                  disabled={loading}
                >
                  Apply
                </button>
              </div>

              {/* Category IDs Bulk Input */}
              <div className="grid grid-cols-3 gap-2">
                <div className="flex gap-1 items-center">
                  <input
                    type="text"
                    placeholder="Cat ID 1"
                    value={bulkCatId1}
                    onChange={(e) => setBulkCatId1(e.target.value)}
                    className="perzsi-input"
                  />
                  <button
                    onClick={handleBulkCatId1Change}
                    className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                    disabled={loading}
                  >
                    ✓
                  </button>
                </div>

                <div className="flex gap-1 items-center">
                  <input
                    type="text"
                    placeholder="Cat ID 2"
                    value={bulkCatId2}
                    onChange={(e) => setBulkCatId2(e.target.value)}
                    className="perzsi-input"
                  />
                  <button
                    onClick={handleBulkCatId2Change}
                    className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                    disabled={loading}
                  >
                    ✓
                  </button>
                </div>

                <div className="flex gap-1 items-center">
                  <input
                    type="text"
                    placeholder="Cat ID 3"
                    value={bulkCatId3}
                    onChange={(e) => setBulkCatId3(e.target.value)}
                    className="perzsi-input"
                  />
                  <button
                    onClick={handleBulkCatId3Change}
                    className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                    disabled={loading}
                  >
                    ✓
                  </button>
                </div>
              </div>

              <div className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'} mt-2`}>
                <div>• Select rows in table to apply to specific items</div>
                <div>• Leave empty to apply to all items if no selection</div>
                <div>• Click 📋 to toggle all selection</div>
              </div>
            </div>
          </div>

          <div className="perzsi-card">
            <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>
              Pagination Settings
            </h3>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              Configure how many products to display per page (1-500).
            </p>

            <div className="space-y-4">
              <div className="flex gap-2 items-center">
                <label htmlFor="pageSize" className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'} whitespace-nowrap`}>
                  Page Size:
                </label>
                <input
                  id="pageSize"
                  type="number"
                  min="1"
                  max="500"
                  value={pageSize}
                  onChange={(e) => setPageSize(parseInt(e.target.value) || 50)}
                  className="perzsi-input flex-1"
                  placeholder="Enter page size (1-500)"
                />
                <button
                  onClick={handlePageSizeChange}
                  className="perzsi-button-primary"
                  disabled={loading}
                >
                  Apply
                </button>
              </div>

              <div className="text-sm text-gray-600">
                Current: <strong>{pageSize}</strong> products per page
              </div>

              <div className="flex gap-2 items-center pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
                <select
                    value={bulkProductStatus}
                    onChange={(e) => setBulkProductStatus(e.target.value)}
                    className="perzsi-input flex-1"
                >
                    <option value="">Select Status</option>
                    <option value="STAGING">STAGING</option>
                    <option value="LIVE">LIVE</option>
                </select>
                <button
                    onClick={handleBulkProductStatusChange}
                    className="perzsi-button-primary"
                    disabled={loading}
                >
                    Apply
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <div className="font-semibold mb-2">
              ❌ {error}
            </div>
            {errorDetails && errorDetails.length > 0 && (
              <div className="mt-3 pt-3 border-t border-red-300">
                <div className="text-sm font-medium mb-2 text-red-800">
                  Error Details ({errorDetails.length} issues):
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {errorDetails.map((detail, index) => (
                    <div
                      key={index}
                      className="text-sm bg-red-50 px-2 py-1 rounded border-l-2 border-red-400"
                    >
                      {detail}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Top Pagination */}
        {products.length > 0 && totalPages > 1 && (
          <div className="mb-4">
            {renderPagination()}
          </div>
        )}

        {/* Products Upload Table */}
        {products.length > 0 && (
          <div className="perzsi-table-container">
            <div className="w-full" style={{ overflowX: 'auto', overflowY: 'visible' }}>
              <table className="perzsi-table" style={{ minWidth: '2000px' }}>
                <thead>
                  <tr>
                    <th className="w-16">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-5 h-5 rounded border-2 border-gray-300 text-perzsi-yellow focus:ring-2 focus:ring-perzsi-yellow focus:border-perzsi-yellow cursor-pointer"
                      />
                    </th>
                    <th>product_id</th>
                    <th>title</th>
                    <th>description</th>
                    <th>product_status</th>
                    <th>created_at</th>
                    <th>updated_at</th>
                    <th>discount_price</th>
                    <th>main_price</th>
                    <th>specification</th>
                    <th>features</th>
                    <th>store_id</th>
                    <th>store_name</th>
                    <th>cat_id_1</th>
                    <th>cat_name_1</th>
                    <th>cat_id_2</th>
                    <th>cat_name_2</th>
                    <th>cat_id_3</th>
                    <th>cat_name_3</th>
                    <th>product_url</th>
                    <th>product_img</th>
                    <th>product_images</th>
                    <th>product_variants</th>
                    <th>Gtin</th>
                    <th>Mpn</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getCurrentPageData().map((product, index) => {
                    const globalIndex = (currentPage - 1) * pageSize + index;

                    return (
                      <tr key={globalIndex} className={`${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${selectedRows.has(globalIndex) ? (isDark ? 'bg-blue-900/20' : 'bg-blue-50') : ''}`}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedRows.has(globalIndex)}
                            onChange={() => handleRowSelection(globalIndex)}
                            className="w-5 h-5 rounded border-2 border-gray-300 text-perzsi-yellow focus:ring-2 focus:ring-perzsi-yellow focus:border-perzsi-yellow cursor-pointer"
                          />
                        </td>
                        <td className={`font-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {product.product_id}
                        </td>
                        <td>
                          <input
                            type="text"
                            value={product.title}
                            onChange={(e) => handleUpdateTitle(index, e.target.value)}
                            placeholder="Enter product title..."
                            className="perzsi-input"
                            style={{ minWidth: '200px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={product.description}
                            onChange={(e) => handleUpdateDescription(index, e.target.value)}
                            placeholder="Enter product description..."
                            className="perzsi-input"
                            style={{ minWidth: '300px' }}
                          />
                        </td>
                        <td className={`font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                          {product.product_status}
                        </td>
                        <td className={`font-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {product.created_at}
                        </td>
                        <td className={`font-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {product.updated_at}
                        </td>
                        <td className={`font-semibold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                          {product.discount_price}
                        </td>
                        <td className={`font-semibold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                          {product.main_price}
                        </td>
                        <td>
                          <input
                            type="text"
                            value={product.specification}
                            onChange={(e) => handleUpdateSpecification(index, e.target.value)}
                            placeholder="Enter product specification..."
                            className="perzsi-input"
                            style={{ minWidth: '250px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={product.features}
                            onChange={(e) => handleUpdateFeatures(index, e.target.value)}
                            placeholder="Enter product features..."
                            className="perzsi-input"
                            style={{ minWidth: '250px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={product.store_id}
                            onChange={(e) => handleUpdateStoreId(index, e.target.value)}
                            placeholder="Enter store ID..."
                            className="perzsi-input"
                          />
                        </td>
                        <td>{product.store_name}</td>
                        <td>
                          <input
                            type="text"
                            value={product.cat_id_1}
                            onChange={(e) => handleUpdateCatId1(index, e.target.value)}
                            placeholder="Enter cat ID..."
                            className={`perzsi-input ${product.cat_name_3 && product.cat_name_3.trim() ? 'opacity-50 bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={product.cat_name_3 && product.cat_name_3.trim() ? true : false}
                          />
                        </td>
                        <td className={`${product.cat_name_3 && product.cat_name_3.trim() ? 'opacity-50 text-gray-400' : ''}`}>
                          {product.cat_name_1}
                        </td>
                        <td>
                          <input
                            type="text"
                            value={product.cat_id_2}
                            onChange={(e) => handleUpdateCatId2(index, e.target.value)}
                            placeholder="Enter cat ID..."
                            className="perzsi-input"
                          />
                        </td>
                        <td>{product.cat_name_2}</td>
                        <td>
                          <input
                            type="text"
                            value={product.cat_id_3}
                            onChange={(e) => handleUpdateCatId3(index, e.target.value)}
                            placeholder="Enter cat ID..."
                            className="perzsi-input"
                          />
                        </td>
                        <td>{product.cat_name_3}</td>
                        <td className="max-w-xs truncate">
                          <a
                            href={product.product_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline text-sm"
                          >
                            View
                          </a>
                        </td>
                        <td className={`font-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'} max-w-xs truncate`} title={product.product_img}>
                          {product.product_img}
                        </td>
                        <td className={`font-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'} max-w-xs truncate`} title={product.product_images}>
                          {product.product_images}
                        </td>
                        <td className={`font-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'} max-w-xs truncate`} title={product.product_variants}>
                          {product.product_variants}
                        </td>
                        <td className={`font-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {product.Gtin}
                        </td>
                        <td className={`font-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {product.Mpn}
                        </td>
                        <td>
                          <button
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors duration-200"
                            onClick={() => removeProduct(index)}
                            title="Remove this product"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {renderPagination()}

        {/* Back Button */}
        <div className="mt-6 text-center">
          <button
            className="perzsi-button-secondary"
            onClick={() => navigate('/store-products')}
          >
            ← Back to Store Products
          </button>
        </div>
      </div>

      {/* Image Preview Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-perzsi-gray">Product Image Preview</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="text-center">
                {selectedImage.map((url, index) => (
                  <img
                    key={index}
                    src={url}
                    alt={`${selectedImageAlt} - ${index + 1}`}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg mb-4"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ))}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  className="perzsi-button-primary"
                  onClick={handleCloseModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductUpload;
